/** @typedef {{ name: string; company: string; email: string; message: string }} ContactInquiryPayload */

/** @typedef {{ method: 'api' | 'web3forms' | 'formsubmit' | 'mailto' }} ContactInquiryResult */

export const CONTACT_INQUIRY_EMAIL =
    process.env.REACT_APP_CONTACT_EMAIL?.trim() || 'contact@codam.co.kr'

/**
 * @param {ContactInquiryPayload} payload
 * @returns {Promise<ContactInquiryResult>}
 */
export async function submitContactInquiry(payload) {
    const customApi = process.env.REACT_APP_CONTACT_API_URL?.trim()
    if (customApi) {
        await postJson(customApi, payload)
        return { method: 'api' }
    }

    const web3Key = process.env.REACT_APP_WEB3FORMS_ACCESS_KEY?.trim()
    if (web3Key) {
        await submitViaWeb3Forms(web3Key, payload)
        return { method: 'web3forms' }
    }

    if (process.env.REACT_APP_FORMSUBMIT_ENABLED === 'true') {
        const formSubmitResult = await submitViaFormSubmit(payload)
        if (formSubmitResult?.method === 'mailto') {
            return formSubmitResult
        }
        return { method: 'formsubmit' }
    }

    openContactMailto(payload)
    return { method: 'mailto' }
}

/**
 * @param {ContactInquiryPayload} payload
 */
export function openContactMailto(payload) {
    window.location.href = buildContactMailtoUrl(payload)
}

/**
 * @param {string} accessKey
 * @param {ContactInquiryPayload} payload
 */
async function submitViaWeb3Forms(accessKey, payload) {
    const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
        },
        body: JSON.stringify({
            access_key: accessKey,
            subject: `[CODAM] 프로젝트 문의 — ${payload.company}`,
            from_name: payload.name,
            name: payload.name,
            company: payload.company,
            email: payload.email,
            message: payload.message,
        }),
    })

    let data = {}
    try {
        data = await res.json()
    } catch {
        data = {}
    }

    if (!res.ok || data.success !== true) {
        const detail =
            typeof data.message === 'string' && data.message.trim()
                ? data.message.trim()
                : `서버 응답 오류 (${res.status})`
        throw new Error(detail)
    }
}

/**
 * @param {ContactInquiryPayload} payload
 * @returns {Promise<ContactInquiryResult | void>}
 */
async function submitViaFormSubmit(payload) {
    const formSubmitEmail = encodeURIComponent(CONTACT_INQUIRY_EMAIL)
    const res = await fetch(`https://formsubmit.co/ajax/${formSubmitEmail}`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
        },
        body: JSON.stringify({
            name: payload.name,
            company: payload.company,
            email: payload.email,
            message: payload.message,
            _subject: `[CODAM] 프로젝트 문의 — ${payload.company}`,
            _template: 'table',
            _captcha: 'false',
        }),
    })

    let data = {}
    try {
        data = await res.json()
    } catch {
        data = {}
    }

    const message = typeof data.message === 'string' ? data.message.trim() : ''

    if (/activation|activate form/i.test(message)) {
        openContactMailto(payload)
        return /** @type {ContactInquiryResult} */ ({ method: 'mailto' })
    }

    const ok =
        res.ok &&
        (data.success === true ||
            data.success === 'true' ||
            (message && /success/i.test(message)))

    if (ok) {
        return
    }

    throw new Error(
        message || `서버 응답 오류 (${res.status}). 이메일(${CONTACT_INQUIRY_EMAIL})로 문의해 주세요.`,
    )
}

/**
 * @param {string} url
 * @param {ContactInquiryPayload} payload
 */
async function postJson(url, payload) {
    const res = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
        },
        body: JSON.stringify(payload),
    })

    if (!res.ok) {
        let detail = ''
        try {
            const body = await res.json()
            detail = body?.message || body?.error || ''
        } catch {
            try {
                detail = await res.text()
            } catch {
                detail = ''
            }
        }
        throw new Error(
            detail?.trim() ||
                `문의 전송에 실패했습니다. (${res.status}) 이메일(${CONTACT_INQUIRY_EMAIL})로 문의해 주세요.`,
        )
    }
}

/**
 * @param {ContactInquiryPayload} payload
 * @returns {string}
 */
export function buildContactMailtoUrl(payload) {
    const subject = encodeURIComponent(`[CODAM] 프로젝트 문의 — ${payload.company}`)
    const body = encodeURIComponent(
        [
            `담당자: ${payload.name}`,
            `회사: ${payload.company}`,
            `이메일: ${payload.email}`,
            '',
            '문의 내용:',
            payload.message,
        ].join('\n'),
    )
    return `mailto:${CONTACT_INQUIRY_EMAIL}?subject=${subject}&body=${body}`
}
